import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture } from 'date-fns';
import type { ReactNode } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

import type { useIsBoostAffordable_anyCard } from './__generated__/useIsBoostAffordable.graphql';

type WithCooldownProps = {
  hasCooldown: true;
  cooldownMessage: ReactNode;
  cooldownPercent: number;
};
type WithoutCooldownProps = {
  hasCooldown: false;
  cooldownMessage: null;
  cooldownPercent: null;
};

type ReturnProps = {
  isBoostable: boolean;
  affordable: boolean;
  xpNeeded: number;
} & (WithCooldownProps | WithoutCooldownProps);

const hasCardCooldown = (
  card: useIsBoostAffordable_anyCard
): card is useIsBoostAffordable_anyCard & { nextLevelUpAvailableAt: Date } => {
  return !!(
    card.nextLevelUpAvailableAt && isFuture(card.nextLevelUpAvailableAt)
  );
};

export const useIsBoostAffordable = () => {
  const { currentUser } = useCurrentUserContext();

  return (
    anyCard: useIsBoostAffordable_anyCard,
    balanceUsed = 0
  ): ReturnProps => {
    const rarityXpBalance = currentUser?.xpBalances?.find(
      ({ currency }) => inGameCurrencyToRarity[currency] === anyCard.rarity
    );

    const xpNeeded = (anyCard.xpNeededForNextGrade || 0) - anyCard.xp;

    const hasCooldown = hasCardCooldown(anyCard);
    const now = new Date();

    const affordable = (rarityXpBalance?.amount || 0) - balanceUsed >= xpNeeded;

    return {
      isBoostable: !hasCooldown && affordable,
      affordable,
      xpNeeded,
      ...(hasCooldown
        ? {
            hasCooldown: true,
            cooldownMessage: <TimeLeft time={anyCard.nextLevelUpAvailableAt} />,
            cooldownPercent:
              (now.getTime() - (anyCard.lastLeveledUpAt?.getTime() || 0)) /
              (anyCard.nextLevelUpAvailableAt.getTime() -
                (anyCard.lastLeveledUpAt?.getTime() || 0)),
          }
        : { hasCooldown: false, cooldownMessage: null, cooldownPercent: null }),
    };
  };
};

useIsBoostAffordable.fragments = {
  anyCard: gql`
    fragment useIsBoostAffordable_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      xp
      xpNeededForNextGrade
      lastLeveledUpAt
      nextLevelUpAvailableAt
    }
  ` as TypedDocumentNode<useIsBoostAffordable_anyCard>,
};
